import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Crown, CheckCircle2, AlertTriangle, AlertOctagon, Sparkles, Plus, X, Calendar, ShieldCheck, Zap, Users, Car, Ban, Receipt } from "lucide-react";

const STATUS_META = {
  trial_active:   { label: "Free Trial",      tone: "emerald", icon: ShieldCheck },
  trial_expired:  { label: "Trial Expired",   tone: "rose",    icon: Ban },
  active:         { label: "Active",          tone: "emerald", icon: CheckCircle2 },
  manual_paid:    { label: "Active (Manual)", tone: "emerald", icon: CheckCircle2 },
  past_due:       { label: "Past Due",        tone: "rose",    icon: AlertOctagon },
  grace_period:   { label: "Grace Period",    tone: "amber",   icon: AlertTriangle },
  cancelled:      { label: "Cancelled",       tone: "stone",   icon: Ban },
  suspended:      { label: "Suspended",       tone: "rose",    icon: Ban },
};

const TONE_BG = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  amber:   "bg-amber-50 border-amber-200 text-amber-900",
  rose:    "bg-rose-50 border-rose-200 text-rose-900",
  stone:   "bg-stone-100 border-stone-200 text-stone-800",
};

const WARN_TONE = {
  warning: "bg-amber-50 border-amber-200 text-amber-900",
  danger:  "bg-rose-50 border-rose-200 text-rose-900",
};

const SIMULATE_TARGETS = [
  { value: "trial_active",  label: "Trial active" },
  { value: "grace_period",  label: "Grace period" },
  { value: "trial_expired", label: "Trial expired" },
  { value: "past_due",      label: "Past due" },
  { value: "active",        label: "Active" },
];

export default function Billing() {
  const { user, refresh } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [status, setStatus] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, d] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/catalogue"),
        api.get("/billing/diagnostics").catch(() => ({ data: null })),
      ]);
      setStatus(s.data);
      setCatalogue(c.data);
      setDiagnostics(d.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  // Phase 15: return-from-checkout handler. If the URL has ?payment=success&intent_id=…
  // we poll /checkout/status/:id until paid (or 5 attempts), then refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!isOwner) return;
    const payment = searchParams.get("payment");
    const intentId = searchParams.get("intent_id");
    if (!payment || !intentId) return;
    if (payment === "cancel") {
      toast.message("Payment cancelled. You can try again any time.");
      const next = new URLSearchParams(searchParams);
      next.delete("payment"); next.delete("intent_id"); next.delete("session_id");
      setSearchParams(next, { replace: true });
      return;
    }
    if (payment !== "success") return;

    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${intentId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          toast.success("Payment confirmed: your plan / add-on is active!");
          await load();
          if (refresh) await refresh();
          const next = new URLSearchParams(searchParams);
          next.delete("payment"); next.delete("intent_id"); next.delete("session_id");
          setSearchParams(next, { replace: true });
          return;
        }
        if (attempts >= 6) {
          toast.message("Payment is still processing. We'll update once the provider confirms.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const wrap = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); if (refresh) await refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!isOwner) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900" data-testid="billing-no-access">
        Only the business owner can view billing & plans.
      </div>
    );
  }

  if (loading || !status || !catalogue) return <div className="text-slate-500" data-testid="billing-loading">Loading billing…</div>;

  const plans = catalogue.plans;
  const addons = catalogue.addons;
  // Server-decided (fail closed): only an explicit `true` enables either.
  const checkoutAvailable = status.checkout_available === true;
  const testControlsEnabled = status.test_controls_enabled === true;
  const statusMeta = STATUS_META[status.subscription_status] || STATUS_META.cancelled;
  const StatusIcon = statusMeta.icon;
  const usedPct = status.fleet.unlimited ? 0 : Math.round(((status.fleet.used || 0) / Math.max(1, status.fleet.total || 1)) * 100);
  const fleetTone = status.fleet.unlimited
    ? "emerald"
    : usedPct >= 100 ? "rose" : usedPct >= 80 ? "amber" : "emerald";

  return (
    <div className="space-y-8 max-w-5xl" data-testid="billing-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold inline-flex items-center gap-3">
          <Crown className="w-7 h-7 text-amber-500" />
          Plans & Billing
        </h1>
        <p className="text-slate-600 mt-1">Owner-only. Business pricing is never shown to renters or staff.</p>
      </div>

      {/* WARNINGS */}
      {status.warnings.length > 0 && (
        <div className="space-y-2" data-testid="billing-warnings">
          {status.warnings.map((w, i) => (
            <div key={i} data-testid={`billing-warning-${w.code}`}
                 className={`rounded-xl border p-3 text-sm flex items-start gap-2 ${WARN_TONE[w.level] || WARN_TONE.warning}`}>
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* CURRENT PLAN CARD */}
      <div className={`rounded-2xl border p-6 ${TONE_BG[statusMeta.tone]}`} data-testid="current-plan-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-70">Current plan</div>
            <h2 className="font-display text-2xl font-bold mt-1">
              {status.plan.name}{" "}
              <span className="text-base font-medium opacity-70">
                {status.plan.price_php_monthly === 0
                  ? "- Free forever"
                  : `- ₱${status.plan.price_php_monthly.toLocaleString()}/month`}
              </span>
            </h2>
            <div className="mt-2 inline-flex items-center gap-2 text-sm">
              <StatusIcon className="w-4 h-4" /> <span className="font-medium">{statusMeta.label}</span>
              {status.trial_days_left !== null && status.subscription_status === "trial_active" && (
                <span data-testid="trial-days-left" className="ml-1">· {status.trial_days_left} day(s) left</span>
              )}
              {status.current_period_end && status.subscription_status !== "trial_active" && (
                <span className="ml-1 inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Renews {new Date(status.current_period_end).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <MiniStat
            icon={Car}
            label="Fleet capacity"
            value={status.fleet.unlimited
              ? "Unlimited"
              : `${status.fleet.used} / ${status.fleet.total} pts`}
            tone={fleetTone}
            testId="mini-fleet"
            sub={!status.fleet.unlimited && status.fleet.addon_points
              ? `Includes ${status.fleet.addon_points} bonus`
              : null}
          />
          <MiniStat
            icon={Users}
            label="Staff used"
            value={status.staff.unlimited ? `${status.staff.used} (unlimited)` : `${status.staff.used} / ${status.staff.limit}`}
            tone={status.staff.over_limit ? "rose" : "emerald"}
            testId="mini-staff"
          />
          <MiniStat
            icon={Sparkles}
            label="Active add-ons"
            value={status.addons.filter((a) => a.status === "active").length}
            tone="emerald"
            testId="mini-addons"
          />
        </div>

        {/* Fleet breakdown */}
        {!status.fleet.unlimited && status.fleet.breakdown.length > 0 && (
          <div className="mt-5" data-testid="fleet-breakdown">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-70 mb-1">Fleet point breakdown</div>
            <div className="text-xs space-y-0.5">
              {status.fleet.breakdown.map((b) => (
                <div key={b.category} className="flex justify-between" data-testid={`fleet-row-${b.category}`}>
                  <span>{b.category}: {b.count} × {b.cost_each}</span>
                  <span className="font-medium">{b.subtotal} pts</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-current/20 pt-1 mt-1 font-semibold">
                <span>Total</span>
                <span>{status.fleet.used} / {status.fleet.total} pts</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PLAN COMPARISON */}
      <section data-testid="plan-comparison">
        <h2 className="font-display text-xl font-semibold">Plans</h2>
        <p className="text-sm text-slate-600 mt-1">
          <span className="font-medium text-slate-800">Prevent one lost booking and ConfirmPH can already pay for itself.</span>{" "}
          Switch any time.
        </p>
        {!checkoutAvailable && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
               data-testid="billing-checkout-unavailable">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Online payment isn't available yet. To move to Basic or Pro, or to add fleet points, contact ConfirmPH and we'll activate it for you.</span>
          </div>
        )}
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.values(plans).map((p) => (
            <PlanCard
              key={p.key}
              plan={p}
              currentPlanType={status.plan_type}
              busy={busy}
              paidCheckoutAvailable={checkoutAvailable}
              onUpgrade={async () => {
                if (p.key === "enterprise") {
                  await wrap(() => api.post("/billing/upgrade", { plan: "enterprise" }),
                              "Enterprise request recorded. We'll be in touch.");
                  return;
                }
                if (p.key === "free") {
                  // Free is a graceful downgrade: no checkout.
                  if (!window.confirm("Switch to the Free plan? You'll keep your data but be limited to 3 fleet points and 1 employee.")) return;
                  await wrap(() => api.post("/billing/upgrade", { plan: "free" }),
                              "Switched to the Free plan.");
                  return;
                }
                // Phase 15: go through /billing/checkout/plan.
                // Mock provider upgrades immediately; real provider returns a checkout_url.
                setBusy(true);
                try {
                  const origin_url = window.location.origin;
                  const r = await api.post("/billing/checkout/plan",
                                            { plan: p.key, period_days: 30, origin_url });
                  if (r.data.mode === "mock") {
                    toast.success(`Switched to ${p.name} (test checkout: marked paid 30 days)`);
                    await load();
                    if (refresh) await refresh();
                  } else if (r.data.checkout_url) {
                    toast.message(`Redirecting to ${r.data.mode}…`);
                    window.location.href = r.data.checkout_url;
                  }
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally { setBusy(false); }
              }}
            />
          ))}
        </div>
      </section>

      {/* EXTRA FLEET POINTS */}
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="fleet-capacity-explainer">
        <h3 className="font-display text-lg font-semibold">How Fleet Capacity works</h3>
        <p className="text-sm text-slate-700 mt-1 leading-relaxed">
          Smaller vehicles use fewer points. Larger vehicles use more points. This keeps pricing fair for motor rentals, car rentals, and mixed fleets, so a motor-only operator isn't punished by unused sedan/SUV slots.
        </p>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">3 points (Free)</div>
            <ul className="mt-2 text-sm text-slate-700 space-y-0.5">
              <li>· 1 sedan / hatchback</li>
              <li>· or 3 motorcycles</li>
              <li>· or any mixed fleet up to 3 pts</li>
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">15 points (Basic)</div>
            <ul className="mt-2 text-sm text-slate-700 space-y-0.5">
              <li>· 15 motors</li>
              <li>· or 5 sedans</li>
              <li>· or 3 SUVs</li>
              <li>· or any mixed fleet up to 15 pts</li>
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">50 points (Pro)</div>
            <ul className="mt-2 text-sm text-slate-700 space-y-0.5">
              <li>· 50 motors</li>
              <li>· or 16 sedans</li>
              <li>· or 10 SUVs</li>
              <li>· or any mixed fleet up to 50 pts</li>
            </ul>
          </div>
        </div>
      </section>

      {/* EXTRA FLEET POINTS */}
      <section data-testid="addons-section">
        <h2 className="font-display text-xl font-semibold">Extra Fleet Points</h2>
        <p className="text-sm text-slate-600 mt-1">Need a little more room? Stack with your plan.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          {Object.values(addons).map((a) => (
            <div key={a.key} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`addon-${a.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-display text-lg font-semibold">{a.label}</h3>
                  <p className="text-sm text-slate-600 mt-1">{a.description}</p>
                </div>
                <Zap className="w-5 h-5 text-amber-500" />
              </div>
              <div className="mt-3 text-2xl font-display font-bold">
                ₱{a.price_php.toLocaleString()}
                <span className="text-sm font-medium text-slate-500 ml-1">
                  {a.billing_type === "monthly" ? "/month" : "one-time"}
                </span>
              </div>
              <button
                onClick={async () => {
                  setBusy(true);
                  try {
                    const origin_url = window.location.origin;
                    const r = await api.post("/billing/checkout/addon",
                                              { addon_key: a.key, origin_url });
                    if (r.data.mode === "mock") {
                      toast.success(`Added: ${a.label} (test purchase)`);
                      await load();
                    } else if (r.data.checkout_url) {
                      toast.message(`Redirecting to ${r.data.mode}…`);
                      window.location.href = r.data.checkout_url;
                    }
                  } catch (e) {
                    toast.error(formatApiError(e));
                  } finally { setBusy(false); }
                }}
                disabled={busy || !checkoutAvailable}
                data-testid={`addon-buy-${a.key}`}
                className="mt-4 w-full px-4 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60"
              >
                {checkoutAvailable ? (
                  <><Plus className="w-4 h-4 inline -mt-0.5 mr-1" />Purchase</>
                ) : "Contact ConfirmPH to add"}
              </button>
            </div>
          ))}
        </div>

        {status.addons.length > 0 && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="my-addons">
            <h3 className="font-display text-base font-semibold mb-2 inline-flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Your add-ons
            </h3>
            <ul className="divide-y divide-slate-100 -mx-2">
              {status.addons.map((a) => (
                <li key={a.id} data-testid={`my-addon-${a.id}`} className="px-2 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <b>{a.add_on_type === "permanent_fleet_points" ? "+10 permanent" : "+10 monthly"}</b>
                    <span className="text-slate-500 ml-2">· ₱{a.price_php} · {a.status}</span>
                    {a.current_period_end && <span className="text-slate-500 ml-2">· until {new Date(a.current_period_end).toLocaleDateString()}</span>}
                  </span>
                  {a.status === "active" && (
                    <button
                      onClick={() => wrap(() => api.post(`/billing/addons/${a.id}/cancel`),
                                            "Add-on cancelled")}
                      data-testid={`addon-cancel-${a.id}`}
                      className="text-xs px-2 py-1 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Cancel
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* PROVIDER READINESS / DIAGNOSTICS */}
      {diagnostics && (
        <section data-testid="diagnostics-section" className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Provider readiness
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Real providers (Postmark for email, PayMongo / Stripe for payments) can be enabled
            by setting environment variables. Without those, the platform stays in mock mode.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            {Object.entries(diagnostics.providers).map(([key, p]) => (
              <div key={key} data-testid={`diag-provider-${key}`}
                   className={`rounded-lg border p-3 ${
                     p.configured
                       ? "border-emerald-200 bg-emerald-50"
                       : "border-amber-200 bg-amber-50"
                   }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{key}</span>
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${
                    p.configured ? "text-emerald-700" : "text-amber-700"
                  }`}>
                    {p.configured ? "Configured" : (p.active_provider === "mock" ? "Mock" : "Not configured")}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1">{p.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
            <Tile label="Total notifications" value={diagnostics.notifications.total} />
            <Tile label="Failed notifications" value={diagnostics.notifications.failed} tone={diagnostics.notifications.failed > 0 ? "rose" : "emerald"} />
            <Tile label="Skipped (idempotent)" value={diagnostics.dedupe.skipped_reminders} />
          </div>
          {Object.keys(diagnostics.rate_limits.scopes_with_events).length > 0 && (
            <details className="mt-4 text-xs text-slate-600" data-testid="diag-rate-limits">
              <summary className="cursor-pointer font-medium">Rate-limit events (recent)</summary>
              <ul className="mt-2 grid sm:grid-cols-2 gap-1">
                {Object.entries(diagnostics.rate_limits.scopes_with_events).map(([k, n]) => (
                  <li key={k} className="font-mono">{k}: {n}</li>
                ))}
              </ul>
            </details>
          )}
          <p className="text-[11px] text-slate-500 mt-3">
            Environment: <code>{diagnostics.environment}</code> · No API keys or secrets are exposed on this page.
          </p>
        </section>
      )}

      {/* TEST CONTROLS: never rendered unless the server reports an approved
          test environment; the endpoints themselves also fail closed. */}
      {testControlsEnabled && (
      <section data-testid="billing-simulate-card" className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5">
        <details>
          <summary className="cursor-pointer text-sm font-medium text-slate-700">Test controls (test environment only): simulate status & mark paid</summary>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 mr-1">Simulate:</span>
            {SIMULATE_TARGETS.map((t) => (
              <button
                key={t.value}
                onClick={() => wrap(() => api.post("/billing/test-controls/simulate", { target: t.value }),
                                      `Status set to ${t.label}`)}
                disabled={busy}
                data-testid={`simulate-${t.value}`}
                className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-100"
              >
                {t.label}
              </button>
            ))}
            <span className="mx-2 h-4 border-l border-slate-300" />
            <button
              onClick={() => wrap(() => api.post("/billing/test-controls/mark-paid", { period_days: 30 }),
                                    "Marked paid for 30 days")}
              disabled={busy}
              data-testid="billing-mark-paid"
              className="text-xs px-3 py-1.5 rounded-md border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"
            >
              Mark paid +30 days
            </button>
            <button
              onClick={() => wrap(() => api.post("/billing/cancel"),
                                    "Subscription cancelled")}
              disabled={busy}
              data-testid="billing-cancel-sub"
              className="text-xs px-3 py-1.5 rounded-md border border-rose-300 bg-white text-rose-700 hover:bg-rose-50"
            >
              Cancel subscription
            </button>
          </div>
        </details>
      </section>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone, testId, sub }) {  return (
    <div data-testid={testId} className={`rounded-lg border bg-white/60 p-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold opacity-70">{label}</div>
        <Icon className="w-4 h-4 opacity-50" />
      </div>
      <div className={`mt-1 text-xl font-display font-semibold ${tone === "rose" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const PLAN_BEST_FOR = {
  free: "Best for solo owners trying ConfirmPH with one car or up to three motorcycles.",
  basic: "Best for small rental shops that want confirmation links, reminders, and booking protection.",
  pro: "Best for growing rentals that want public visibility, more staff, more fleet capacity, and in-app bookings.",
  enterprise: "Best for larger fleets, branches, and operators needing advanced controls.",
};

function PlanCard({ plan, currentPlanType, busy, onUpgrade, paidCheckoutAvailable }) {
  const current = plan.key === currentPlanType;
  const bestFor = PLAN_BEST_FOR[plan.key];
  const isFree = plan.key === "free";
  const needsCheckout = !isFree && plan.key !== "enterprise";
  const blocked = needsCheckout && !paidCheckoutAvailable;
  return (
    <div data-testid={`plan-card-${plan.key}`} className={`rounded-2xl border p-5 bg-white ${
      current ? "border-[#1B4332] ring-1 ring-[#1B4332]/30" : "border-slate-200"
    }`}>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold">{plan.name}</h3>
        {current && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#1B4332] text-white font-medium" data-testid={`plan-current-${plan.key}`}>Current</span>}
      </div>
      <div className="mt-2 text-2xl font-display font-bold">
        {isFree
          ? <span>₱0 <span className="text-sm font-medium text-slate-500">forever</span></span>
          : plan.price_label
            ? plan.price_label
            : <>₱{plan.price_php_monthly.toLocaleString()}<span className="text-sm font-medium text-slate-500"> / month</span></>}
      </div>
      {bestFor && (
        <p className="mt-2 text-xs text-slate-600 leading-relaxed border-l-2 border-[#1B4332]/30 pl-2"
           data-testid={`plan-best-for-${plan.key}`}>
          <span className="font-semibold text-slate-700 uppercase tracking-wider text-[10px]">Best for</span>{" "}
          {bestFor}
        </p>
      )}
      <p className="mt-2 text-xs text-slate-500">{plan.summary}</p>
      <ul className="mt-3 space-y-1 text-sm text-slate-700">
        <li className="font-medium text-slate-900">
          {plan.staff_limit === null ? "Unlimited staff" : `${plan.staff_limit} staff accounts`}
          {" · "}
          {plan.fleet_points === null ? "Unlimited fleet" : `${plan.fleet_points} fleet points`}
        </li>
        {plan.features.slice(0, 5).map((f) => (
          <li key={f} className="text-xs text-slate-600 flex items-start gap-1.5">
            <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-500 flex-shrink-0" />{f}
          </li>
        ))}
        {plan.features.length > 5 && (
          <li className="text-xs text-slate-500">+{plan.features.length - 5} more</li>
        )}
      </ul>
      <button
        onClick={onUpgrade}
        disabled={busy || current || blocked}
        data-testid={`plan-switch-${plan.key}`}
        className={`mt-4 w-full px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60 ${
          current ? "bg-slate-200 text-slate-600" : "btn-brand"
        }`}
      >
        {current
          ? "Current plan"
          : blocked
            ? "Contact ConfirmPH to upgrade"
            : plan.key === "enterprise"
            ? "Contact us"
            : plan.key === "free"
              ? "Downgrade to Free"
              : `Switch to ${plan.name}`}
      </button>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const cls = {
    rose:    "border-rose-200 bg-rose-50 text-rose-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-800";
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-70">{label}</div>
      <div className="mt-1 text-xl font-display font-semibold">{value ?? 0}</div>
    </div>
  );
}
